import React from 'react';
import { Building2, Mail, Phone, UserRound } from 'lucide-react';
import type { KundenportalDaten } from '../../../services/sifa/kundenportalTypes';
import { formatZeit } from '../../../services/sifa/kundenportalLogic';
import { zaehlerUebersicht, type PortalTab } from './kundenportalSeiteLogic';

const Kachel: React.FC<{ wert: number; label: string; ton?: 'rot' | 'gelb' | 'gruen' | 'grau'; onClick?: () => void }> = ({ wert, label, ton = 'grau', onClick }) => {
  const farbe =
    ton === 'rot' ? 'bg-red-50 text-red-700' : ton === 'gelb' ? 'bg-amber-50 text-amber-700' : ton === 'gruen' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-700';
  const inhalt = (
    <>
      <p className="text-2xl font-black">{wert}</p>
      <p className="text-xs font-semibold opacity-80">{label}</p>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={`rounded-xl p-3 text-left ${farbe} hover:opacity-90 min-h-[64px]`}>
      {inhalt}
    </button>
  ) : (
    <div className={`rounded-xl p-3 ${farbe} min-h-[64px]`}>{inhalt}</div>
  );
};

const Zeile: React.FC<{ label: string; wert: React.ReactNode }> = ({ label, wert }) =>
  wert === null || wert === undefined || wert === '' ? null : (
    <div className="flex flex-col sm:flex-row sm:gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
      <dt className="text-sm text-slate-500 sm:w-48 shrink-0">{label}</dt>
      <dd className="text-sm font-semibold text-slate-800 break-words">{wert}</dd>
    </div>
  );

/** Erste Karte nach der Anmeldung: Kennzahlen, Betriebsdaten, Ansprechpartner der SiFa. */
export const UebersichtTab: React.FC<{ daten: KundenportalDaten; onTab: (t: PortalTab) => void }> = ({ daten, onTab }) => {
  const z = zaehlerUebersicht(daten);
  const b = daten.betrieb;
  const k = daten.sifaKontakt;
  const gruppe = b.betreuungsgruppe ? `Betreuungsgruppe ${b.betreuungsgruppe}` : null;

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h2 className="text-sm font-black uppercase tracking-wide text-slate-400">Auf einen Blick</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Kachel wert={z.mitarbeiterAktiv} label="Aktive Mitarbeiter" onClick={() => onTab('mitarbeiter')} />
          <Kachel wert={z.nachweiseGueltig} label="mit gültigem Nachweis" ton="gruen" onClick={() => onTab('unterweisungen')} />
          <Kachel wert={z.nachweiseBald + z.nachweiseAbgelaufen + z.ohneNachweis} label="Nachweis fällig oder fehlt" ton={z.nachweiseAbgelaufen + z.ohneNachweis > 0 ? 'rot' : z.nachweiseBald > 0 ? 'gelb' : 'grau'} onClick={() => onTab('mitarbeiter')} />
          <Kachel wert={z.kurseAktiv} label="Unterweisungen aktiv" onClick={() => onTab('unterweisungen')} />
          <Kachel wert={z.fristenOffen} label="Offene Fristen" onClick={() => onTab('fristen')} />
          <Kachel wert={z.fristenUeberfaellig} label="davon überfällig" ton={z.fristenUeberfaellig > 0 ? 'rot' : 'grau'} onClick={() => onTab('fristen')} />
          <Kachel wert={z.fristenBald} label="in 60 Tagen fällig" ton={z.fristenBald > 0 ? 'gelb' : 'grau'} onClick={() => onTab('fristen')} />
          <Kachel wert={z.dokumente} label="Dokumente" onClick={() => onTab('dokumente')} />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-black uppercase tracking-wide text-slate-400 flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Betrieb
        </h2>
        <dl className="bg-slate-50 rounded-xl px-3 py-1">
          <Zeile label="Betrieb" wert={b.name} />
          <Zeile label="Auftraggeber" wert={b.kundeName && b.kundeName !== b.name ? b.kundeName : null} />
          <Zeile label="Anschrift" wert={b.adresse} />
          <Zeile label="Branche" wert={b.branche} />
          <Zeile label="Berufsgenossenschaft" wert={b.berufsgenossenschaft} />
          <Zeile label="Beschäftigte" wert={b.beschaeftigte !== null ? `${b.beschaeftigte}${gruppe ? ` · ${gruppe}` : ''}` : gruppe} />
          <Zeile label="Betriebsarzt" wert={b.betriebsarzt} />
          <Zeile label="Sicherheitsbeauftragte" wert={b.sicherheitsbeauftragte} />
          <Zeile label="Ersthelfer" wert={b.ersthelfer !== null ? String(b.ersthelfer) : null} />
          <Zeile label="Projektnummer" wert={b.projektnummer} />
        </dl>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-black uppercase tracking-wide text-slate-400 flex items-center gap-2">
          <UserRound className="w-4 h-4" /> Ihre Fachkraft für Arbeitssicherheit
        </h2>
        <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
          <p className="font-semibold text-slate-800">{k.name || daten.bueroName || '—'}</p>
          {k.name && daten.bueroName && <p className="text-sm text-slate-500">{daten.bueroName}</p>}
          {k.telefon && (
            <a href={`tel:${k.telefon.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-sm text-blue-700 min-h-[36px]">
              <Phone className="w-4 h-4" /> {k.telefon}
            </a>
          )}
          {k.email && (
            <a href={`mailto:${k.email}`} className="flex items-center gap-2 text-sm text-blue-700 min-h-[36px] break-all">
              <Mail className="w-4 h-4 shrink-0" /> {k.email}
            </a>
          )}
          {!k.telefon && !k.email && <p className="text-sm text-slate-500">Kontaktdaten sind nicht hinterlegt.</p>}
        </div>
      </section>

      <p className="text-xs text-slate-400">Stand: {formatZeit(daten.geladenAm)}</p>
    </div>
  );
};
